import React, { useState, useEffect } from 'react';
import {
  PaymentElement,
  useStripe,
  useElements
} from '@stripe/react-stripe-js';

// Type declaration for window.MSStream
declare global {
  interface Window {
    MSStream?: any;
  }
}

interface CheckoutFormProps {
  formData: {
    donorName: string;
    donorEmail: string;
    donorMessage: string;
    recipientDesignation: string;
    recipientEmail: string;
    giftType: string;
    donationAmount: string;
  };
  onGoBack: () => void;
}

const CheckoutForm: React.FC<CheckoutFormProps> = ({ formData, onGoBack }) => {
  const stripe = useStripe();
  const elements = useElements();

  const [message, setMessage] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  
  // Function to get the appropriate redirect URL
  const getRedirectUrl = (path: string): string => {
    const apiUrl = process.env.REACT_APP_API_URL;
    if (apiUrl && !apiUrl.includes(window.location.origin)) {
      return `${apiUrl}${path}`;
    }
    return `${window.location.origin}${path}`;
  };
  
  useEffect(() => {
    if (!stripe) {
      return;
    }

    const clientSecret = new URLSearchParams(window.location.search).get(
      'payment_intent_client_secret'
    );

    if (!clientSecret) {
      return;
    }

    stripe.retrievePaymentIntent(clientSecret).then(({ paymentIntent }) => {
      if (!paymentIntent) return;
      
      switch (paymentIntent.status) {
        case 'succeeded':
          setMessage('Pagament completat!');
          break;
        case 'processing':
          setMessage('El seu pagament s\'està processant.');
          break;
        case 'requires_payment_method':
          setMessage('El seu pagament no s\'ha pogut completar, si us plau, torneu-ho a provar.');
          break;
        default:
          setMessage('Alguna cosa ha anat malament.');
          break;
      }
    });
  }, [stripe]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!stripe || !elements) {
      // Stripe.js hasn't yet loaded.
      return;
    }

    setIsLoading(true);

    const { error } = await stripe.confirmPayment({
      elements,
      confirmParams: {
        // Success redirect URL using the appropriate path
        return_url: getRedirectUrl('/success.html'),
        receipt_email: formData.donorEmail,
        payment_method_data: {
          billing_details: {
            name: formData.donorName,
            email: formData.donorEmail
          }
        }
      }
    });

    if (error) {
      if (error.type === "card_error" || error.type === "validation_error") {
        // Show validation errors in the form
        setMessage(error.message || "Hi ha hagut un error");
      } else {
        // Only redirect to failure page for unexpected errors or actual payment failures
        setMessage("Hi ha hagut un error inesperat");
        window.location.href = getRedirectUrl('/failure.html');
      }
    }

    setIsLoading(false);
  };

  return (
    <form id="payment-form" onSubmit={handleSubmit}>
      <button 
        type="button" 
        className="back-button" 
        onClick={onGoBack}
      >
        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m12 19-7-7 7-7"></path><path d="M19 12H5"></path></svg>
        Tornar a la informació
      </button>
      
      <div className="form-section enhanced-form-section">
        <div className="payment-summary">
          <h3 className="section-title enhanced-section-title">
            <div className="section-icon">
              <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 12V8H6a2 2 0 0 1-2-2c0-1.1.9-2 2-2h12v4"></path><path d="M4 6v12c0 1.1.9 2 2 2h14v-4"></path><path d="M18 12a2 2 0 0 0-2 2c0 1.1.9 2 2 2h4v-4h-4z"></path></svg>
            </div>
            Resum de la donació
          </h3>
          <div className="summary-details">
            <div className="summary-row">
              <span className="summary-label">Import:</span>
              <span className="summary-value">{formData.donationAmount}€</span>
            </div>
            <div className="summary-row">
              <span className="summary-label">Destinatari:</span>
              <span className="summary-value">{formData.recipientDesignation || "No especificat"}</span>
            </div>
            <div className="summary-row">
              <span className="summary-label">Email del donant:</span>
              <span className="summary-value">{formData.donorEmail}</span>
            </div>
          </div>
        </div>
      </div>
      
      <div className="form-section enhanced-form-section">
        <h3 className="section-title enhanced-section-title">
          <div className="section-icon">
            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect width="20" height="14" x="2" y="5" rx="2"></rect><line x1="2" x2="22" y1="10" y2="10"></line></svg>
          </div>
          Detalls de pagament
        </h3>
        <PaymentElement id="payment-element" />
      </div>
      
      <button 
        disabled={isLoading || !stripe || !elements} 
        id="submit" 
        className="payment-button"
      >
        <span id="button-text">
          {isLoading ? (
            <div className="spinner-container">
              <div className="spinner"></div>
              <span>Processant...</span>
            </div>
          ) : (
            <>
              <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14"></path><path d="m12 5 7 7-7 7"></path></svg>
              Completar pagament
            </>
          )}
        </span>
      </button>
      
      {/* Show any error or success messages */}
      {message && (
        <div id="payment-message" className={message.includes('error') ? 'error' : ''}>
          {message}
        </div>
      )}
    </form>
  );
};

export default CheckoutForm; 